import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

import { buildApiUrl } from '../utils/apiConfig';
import { AuthContext } from './authContext';

export const GuestAccessProvider = ({ guestCode, children, onResolveError = null }) => {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [activeUser, setActiveUser] = useState(null);
  const [authenticatedUser, setAuthenticatedUser] = useState(null);

  const [activeTourToShow, setActiveTourToShow] = useState(null);
  const [activeMapToShow, setActiveMapToShow] = useState(null);
  const [activeMapToEdit, setActiveMapToEdit] = useState(null);

  const [userMaps, setUserMaps] = useState([]);
  const [corporateMaps, setCorporateMaps] = useState([]);
  const [userTours, setUserTours] = useState([]);
  const [tourUserTour, setTourUserTour] = useState(null);

  const [accessToken, setAccessToken] = useState('');
  const [accessTokenExpiresAt, setAccessTokenExpiresAt] = useState(0);
  const [tileToken, setTileToken] = useState('');
  const [tileTokenExpiresAt, setTileTokenExpiresAt] = useState(0);

  const [resolvedTarget, setResolvedTarget] = useState(null);
  const [resolveError, setResolveError] = useState('');
  const [isOnline, setIsOnline] = useState(true);
  const [isFullTourPending, setIsFullTourPending] = useState(false);

  const resolveInFlightRef = useRef(false);
  const fullTourInFlightRef = useRef(false);
  const fullTourLoadedRef = useRef(false);
  const activeTourRef = useRef(null);
  const accessTokenRef = useRef('');

  useEffect(() => {
    activeTourRef.current = activeTourToShow;
  }, [activeTourToShow]);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const isMobileViewport = useCallback(() => (
    typeof window !== 'undefined' && window.innerWidth <= 768
  ), []);

  const scheduleIdleTask = useCallback((callback, timeout = 1800) => {
    if (typeof window === 'undefined') return null;
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(callback, { timeout });
      return () => window.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(callback, timeout);
    return () => window.clearTimeout(id);
  }, []);

  const loadMobileInitialContext = useCallback(async (tokenOverride = '') => {
    const token = tokenOverride || accessTokenRef.current;
    if (!token) return;

    try {
      const response = await axios({
        url: buildApiUrl('guest/mobile-initial/'),
        method: 'get',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const mobileTour = response?.data?.tour;
      if (mobileTour && response?.data?.target?.type === 'tour') {
        const currentTour = activeTourRef.current;
        const wouldDowngradeFullTour =
          Boolean(mobileTour?._is_lite) &&
          (
            fullTourLoadedRef.current ||
            (currentTour && !currentTour?._is_lite)
          );
        if (wouldDowngradeFullTour) return;

        setActiveTourToShow(mobileTour);
        setTourUserTour(mobileTour);
        setUserTours([mobileTour]);
      }
    } catch (error) {
      console.log('Guest mobile initial tour load failed:', error);
    }
  }, []);

  const loadFullTourContext = useCallback(async (tokenOverride = '') => {
    const token = tokenOverride || accessTokenRef.current;
    if (!token) {
      setIsFullTourPending(false);
      return;
    }
    if (fullTourInFlightRef.current || fullTourLoadedRef.current) return;

    fullTourInFlightRef.current = true;
    setIsFullTourPending(true);
    try {
      const response = await axios({
        url: buildApiUrl('guest/context/'),
        method: 'get',
        params: { include_tour: 'full' },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const fullTour = response?.data?.tour;
      if (fullTour && response?.data?.target?.type === 'tour') {
        const tourToApply = activeTourRef.current?.mobile_initial && !fullTour?.mobile_initial
          ? { ...fullTour, mobile_initial: activeTourRef.current.mobile_initial }
          : fullTour;
        setActiveTourToShow(tourToApply);
        setTourUserTour(tourToApply);
        setUserTours([tourToApply]);
        fullTourLoadedRef.current = true;
      }
    } catch (error) {
      console.log('Guest full tour lazy-load failed:', error);
    } finally {
      fullTourInFlightRef.current = false;
      setIsFullTourPending(false);
    }
  }, []);

  const getApiHeaders = useCallback(async () => {
    if (!accessToken) return {};
    return {
      Authorization: `Bearer ${accessToken}`,
    };
  }, [accessToken]);

  const getTileAuthQuery = useCallback(() => {
    if (!tileToken) return '';

    const params = new URLSearchParams();
    params.set('tile_token', tileToken);
    if (resolvedTarget?.map_id) {
      params.set('map_id', String(resolvedTarget.map_id));
    }
    return params.toString();
  }, [resolvedTarget?.map_id, tileToken]);

  const applyResolvedPayload = useCallback((data) => {
    const target = data?.target || null;
    const targetType = target?.type;
    const targetId = target?.id;

    setResolvedTarget(target);

    const nextAccessToken = data?.access_token || '';
    const nextTileToken = data?.tile_token || '';
    const nextAccessExpiresIn = Number(data?.expires_in || 0);
    const nextTileExpiresIn = Number(data?.tile_expires_in || 0);

    if (nextAccessToken) {
      setAccessToken(nextAccessToken);
      setAccessTokenExpiresAt(Date.now() + Math.max(nextAccessExpiresIn, 60) * 1000);
    }

    if (nextTileToken) {
      setTileToken(nextTileToken);
      setTileTokenExpiresAt(Date.now() + Math.max(nextTileExpiresIn, 60) * 1000);
    }

    if (targetType === 'tour' && data?.tour) {
      const incomingTour = data.tour;
      const keepCurrentFullTour =
        Boolean(incomingTour?._is_lite) &&
        fullTourLoadedRef.current &&
        Boolean(activeTourRef.current) &&
        !Boolean(activeTourRef.current?._is_lite);
      const tourToApply = keepCurrentFullTour ? activeTourRef.current : incomingTour;
      const tourWithMobileInitial =
        activeTourRef.current?.mobile_initial && !tourToApply?.mobile_initial
          ? { ...tourToApply, mobile_initial: activeTourRef.current.mobile_initial }
          : tourToApply;

      const guestTourUser = {
        id: targetId,
        object_id: targetId,
        object_type: 'tour_user',
        role: 'guest',
        read: true,
        edit: false,
        capabilities: data?.tile_capabilities?.can_use_satellite_tiles
          ? ['tiles.satellite.use']
          : [],
      };
      setActiveUser(guestTourUser);
      setAuthenticatedUser(guestTourUser);
      setActiveTourToShow(tourWithMobileInitial);
      setTourUserTour(tourWithMobileInitial);
      setUserTours([tourWithMobileInitial]);
      setActiveMapToShow(null);
      if (!incomingTour?._is_lite || keepCurrentFullTour) {
        fullTourLoadedRef.current = true;
        setIsFullTourPending(false);
      } else {
        setIsFullTourPending(true);
      }
      return;
    }

    if (targetType === 'map' && data?.map) {
      fullTourLoadedRef.current = false;
      const guestMapUser = {
        id: targetId,
        object_id: targetId,
        object_type: 'map',
        role: 'guest',
        read: true,
        edit: false,
      };
      setActiveUser(guestMapUser);
      setAuthenticatedUser(guestMapUser);
      setActiveMapToShow(data.map);
      setUserMaps([data.map]);
      setActiveTourToShow(null);
      setTourUserTour(null);
      setUserTours([]);
      setIsFullTourPending(false);
      return;
    }

    if (targetType === 'file' && data?.shared_file) {
      fullTourLoadedRef.current = false;
      const guestFileUser = {
        id: targetId,
        object_id: targetId,
        object_type: 'file',
        role: 'guest',
        read: true,
        edit: false,
      };
      setActiveUser(guestFileUser);
      setAuthenticatedUser(guestFileUser);
      setActiveTourToShow(null);
      setTourUserTour(null);
      setActiveMapToShow(null);
      setIsFullTourPending(false);
      return;
    }

    setActiveUser(null);
    setAuthenticatedUser(null);
    fullTourLoadedRef.current = false;
    setIsFullTourPending(false);
  }, []);

  const resolveGuestCode = useCallback(async () => {
    if (!guestCode) {
      setResolveError('Missing guest code');
      setIsAuthenticating(false);
      return false;
    }

    if (resolveInFlightRef.current) return false;

    resolveInFlightRef.current = true;
    setIsAuthenticating(true);

    try {
      const result = await axios({
        url: buildApiUrl('guest/resolve-code/'),
        method: 'post',
        data: { code: guestCode },
      });

      setResolveError('');
      applyResolvedPayload(result.data || {});
      if (result?.data?.target?.type === 'tour' && result?.data?.tour?._is_lite && result?.data?.access_token) {
        if (isMobileViewport()) {
          scheduleIdleTask(() => {
            void loadMobileInitialContext(result.data.access_token);
          }, 100);
          scheduleIdleTask(() => {
            void loadFullTourContext(result.data.access_token);
          }, 3200);
        } else {
          void loadFullTourContext(result.data.access_token);
        }
      }
      return true;
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Unable to resolve code';
      setResolveError(detail);
      setAccessToken('');
      setTileToken('');
      setActiveUser(null);
      setAuthenticatedUser(null);
      setActiveTourToShow(null);
      setTourUserTour(null);
      setActiveMapToShow(null);
      setIsFullTourPending(false);

      if (typeof onResolveError === 'function') {
        onResolveError(detail);
      }

      return false;
    } finally {
      resolveInFlightRef.current = false;
      setIsAuthenticating(false);
    }
  }, [applyResolvedPayload, guestCode, isMobileViewport, loadFullTourContext, loadMobileInitialContext, onResolveError, scheduleIdleTask]);

  useEffect(() => {
    fullTourLoadedRef.current = false;
    fullTourInFlightRef.current = false;
    setIsFullTourPending(false);
  }, [guestCode]);

  useEffect(() => {
    void resolveGuestCode();
  }, [guestCode]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!accessToken || !guestCode) return;

    const timer = window.setInterval(() => {
      const now = Date.now();
      const refreshThresholdMs = 15 * 1000;
      const shouldRefreshAccess = accessTokenExpiresAt && now >= accessTokenExpiresAt - refreshThresholdMs;
      const shouldRefreshTile = tileTokenExpiresAt && now >= tileTokenExpiresAt - refreshThresholdMs;
      if (shouldRefreshAccess || shouldRefreshTile) {
        void resolveGuestCode();
      }
    }, 30000);

    return () => window.clearInterval(timer);
  }, [accessToken, accessTokenExpiresAt, guestCode, resolveGuestCode, tileTokenExpiresAt]);

  const contextValue = useMemo(() => ({
    userID: null,
    canReadMap: true,
    canEditMap: false,
    activeUser,
    isOnline,
    isAuthenticating,
    isFullTourPending,
    maptilerKey: null,
    keycloakInitialized: true,
    authenticatedUser,
    actingUserId: null,
    isImpersonating: false,
    adminSwitchableUsers: [],
    isUserSwitching: false,

    isAuthenticated: Boolean(accessToken),
    getToken: () => accessToken,
    getAuthHeader: () => (accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    getApiHeaders,
    getTileAuthQuery,

    userMaps,
    corporateMaps,
    userTours,
    tourUserTour,
    activeTourToShow,
    activeMapToShow,
    activeMapToEdit,

    guestCode,
    resolveError,

    isTourUser: () => activeUser?.object_type === 'tour_user',

    handleLogin: () => {},
    handleLogout: () => {},
    validateAndFetchUserData: resolveGuestCode,
    switchToUser: async () => false,
    fetchAdminSwitchableUsers: async () => [],

    setActiveTourToShow,
    setActiveMapToShow,
    setActiveMapToEdit,
    setTourUserTour,
    setUserTours,
    setUserMaps,
    setCorporateMaps,

    fetchUserMaps: async () => [],
    fetchFullMapData: async () => null,
    fetchUserTours: async () => [],
    fetchFullTourData: async () => null,
    fetchTourUserData: async () => null,
    fetchMapInfo: async () => null,
  }), [
    accessToken,
    activeMapToEdit,
    activeMapToShow,
    activeTourToShow,
    activeUser,
    authenticatedUser,
    corporateMaps,
    getApiHeaders,
    getTileAuthQuery,
    guestCode,
    isAuthenticating,
    isFullTourPending,
    isOnline,
    resolveError,
    resolveGuestCode,
    tourUserTour,
    userMaps,
    userTours,
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
